import type { FieldProp } from 'react-typed-form';
import type { StageFormValues } from '../service-order-form';

import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { Button, Modal, ModalBody, ModalFooter, Text } from 'src/components';
import { color } from 'src/constants/color.css';
import { FieldDate } from 'src/form';
import { toDayInput } from 'src/util';

type Props = {
  stage: StageFormValues;
  onSave: (stage: StageFormValues) => void;
  onRemove: () => void;
  onClose: () => void;
};

/**
 * One stage's name and dates, written back into the form on Save.  The dates
 * are held as the plain days the form stores, and read and typed through
 * FieldDate so no timezone ever comes into them.
 */
export default function EditStageModal({
  stage,
  onSave,
  onRemove,
  onClose,
}: Props) {
  const [name, setName] = React.useState(stage.name);
  const [startDate, setStartDate] = React.useState(stage.startDate);
  const [estimatedFinishDate, setEstimatedFinishDate] = React.useState(
    stage.estimatedFinishDate
  );
  const [finishDate, setFinishDate] = React.useState(stage.finishDate);

  const save = () => {
    onSave({
      ...stage,
      name,
      startDate: toDayInput(startDate),
      estimatedFinishDate: toDayInput(estimatedFinishDate),
      finishDate: toDayInput(finishDate),
    });
  };

  return (
    <Modal title="Edit stage" onClose={onClose} fullScreenOnMobile>
      <ModalBody>
        <html.div style={[styles.row]}>
          <html.div style={styles.field}>
            <FieldLabel>Stage name</FieldLabel>
            <html.input
              style={styles.input}
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
              autoFocus
            />
          </html.div>
          <html.div style={styles.field}>
            <FieldDate
              field={dateField('startDate', startDate, setStartDate)}
              label="Start"
            />
          </html.div>
        </html.div>

        <html.div style={[styles.row]}>
          <html.div style={styles.field}>
            <FieldDate
              field={dateField(
                'estimatedFinishDate',
                estimatedFinishDate,
                setEstimatedFinishDate
              )}
              label="Estimated finish"
            />
          </html.div>
          <html.div style={styles.field}>
            <FieldDate
              field={dateField('finishDate', finishDate, setFinishDate)}
              label="Finish"
            />
          </html.div>
        </html.div>
      </ModalBody>

      <ModalFooter
        left={<Button label="Remove stage" variant="text" onClick={onRemove} />}
      >
        <Button
          label="Cancel"
          variant="text"
          colorScheme="black"
          onClick={onClose}
        />
        <Button label="Save" onClick={save} />
      </ModalFooter>
    </Modal>
  );
}

/**
 * A minimal field descriptor over the modal's own date state, so FieldDate can
 * edit a stage copy rather than the live form values, keeping Cancel clean.
 */
function dateField(
  name: string,
  value: string | null,
  set: (value: string | null) => void
): FieldProp<string | null> {
  return {
    name,
    label: '',
    value,
    handleValueChange: set,
  };
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text typeface="body.small.regular" color="neutral80">
      {children}
    </Text>
  );
}

const styles = css.create({
  row: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  input: {
    padding: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    fontSize: 16,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.5,
    color: color.neutral100,
    backgroundColor: color.neutral0,
    outline: 'none',
    width: '100%',
    '::placeholder': {
      color: color.neutral40,
    },
  },
});
